import { ApiService } from '~/services/api/api'
import type { Candidate } from '~/types/Candidate'
import type {
  Interaction,
  InteractionWithoutThread,
  MaybePopulatedInteraction,
  MaybePopulatedInteractionWithoutThread,
  PostInteractionBody,
  Thread,
} from '~/types/Interaction'
import type { WorkspaceActivitySummary } from '~/types/Activity'
import type { Unsaved } from '~/types/Unsaved'
import type { AnyError } from '~/types/Error'

export class InteractionService {
  private readonly apiService = new ApiService()

  // INTERACTION ========================================
  async getInteractionById(simulationId: string, interactionId: string) {
    const response = await this.apiService.get<Interaction>(
      `simulations/${simulationId}/interactions/${interactionId}`,
      { useFallbackAuth: true },
    )
    return response
  }

  async getInteractionsBySimulationId(simulationId: string) {
    const response = await this.apiService.get<InteractionWithoutThread[]>(
      `simulations/${simulationId}/interactions`,
    )
    return response
  }

  async getPopulatedInteractionsBySimulationId(simulationId: string) {
    const response = await this.apiService.get<
      MaybePopulatedInteractionWithoutThread[]
    >(`simulations/${simulationId}/interactions`, {
      params: {
        populate: 'candidate',
      },
    })
    return response
  }

  async getInteractionsUpdatesBySimulationId(
    simulationId: string,
    lastUpdateTimestamp: string,
  ) {
    const response = await this.apiService.get<Interaction[]>(
      `simulations/${simulationId}/interactions`,
      {
        params: {
          updatedAtGt: lastUpdateTimestamp,
        },
      },
    )
    return response
  }

  async getPopulatedInteractionById(
    simulationId: string,
    interactionId: string,
  ) {
    const response = await this.apiService.get<MaybePopulatedInteraction>(
      `simulations/${simulationId}/interactions/${interactionId}`,
      {
        params: {
          populate: 'candidate',
        },
      },
    )
    return response
  }

  async getWorkspaceInteractionsSummary(workspaceId: string) {
    const response = await this.apiService.get<WorkspaceActivitySummary>(
      `workspaces/${workspaceId}/activity-summary`,
    )
    return response
  }

  // DO AN INTERACTION ==================================
  async postInteraction(
    simulationId: string,
    interaction: PostInteractionBody,
  ) {
    const response = await this.apiService.post<
      Interaction,
      PostInteractionBody
    >(`/simulations/${simulationId}/interactions`, interaction, {
      useFallbackAuth: true,
    })
    return response
  }

  async postInteractionCandidateData(
    simulationId: string,
    interactionId: string,
    candidate: Unsaved<Candidate>,
  ) {
    const response = await this.apiService.post<Candidate, Unsaved<Candidate>>(
      `/simulations/${simulationId}/interactions/${interactionId}/candidates`,
      candidate,
      { useFallbackAuth: true },
    )
    return response
  }

  async startInteraction(
    simulationId: string,
    interactionId: string,
    thread: Thread,
  ) {
    const response = await this.apiService.post<Thread, Thread>(
      `/simulations/${simulationId}/interactions/${interactionId}/start`,
      thread,
      { useFallbackAuth: true },
    )
    return response
  }

  async continueInteraction(
    simulationId: string,
    interactionId: string,
    thread: Thread,
  ) {
    const response = await this.apiService.post<Thread, Thread>(
      `/simulations/${simulationId}/interactions/${interactionId}/continue`,
      thread,
      { useFallbackAuth: true },
    )
    return response
  }

  async verifyProceed(
    simulationId: string,
    interactionId: string,
    candidateId: string,
  ) {
    const response = await this.apiService.get<string | boolean>(
      `/simulations/${simulationId}/interactions/${interactionId}/candidates/${candidateId}/verify-proceed`,
      { useFallbackAuth: true },
    )

    // Convert the string 'true' to boolean true
    if (response.data !== null && typeof response.data === 'string') {
      response.data = (response.data === 'true') as boolean
    }

    return response as
      | { data: boolean; error: null }
      | { data: null; error: AnyError }
  }

  async updateInteractionProviderInteractionId(
    simulationId: string,
    interactionId: string,
    providerInteractionId: string,
  ) {
    const response = await this.apiService.patch<
      Interaction,
      { providerInteractionId: string }
    >(
      `/simulations/${simulationId}/interactions/${interactionId}/provider-interaction-id`,
      { providerInteractionId },
      { useFallbackAuth: true },
    )
    return response
  }

  // CANDIDATES ========================================
  async getCandidateById(
    simulationId: string,
    interactionId: string,
    candidateId: string,
  ) {
    const response = await this.apiService.get<Candidate>(
      `simulations/${simulationId}/interactions/${interactionId}/candidates/${candidateId}`,
      { useFallbackAuth: true },
    )
    return response
  }

  // INTERACTION SESSIONS ===============================================
  async getInteractionSession(
    simulationId: string,
    candidateId: string,
    isPreview: boolean,
    testIds: string[],
    assessmentIds: string[],
  ) {
    const response = await this.apiService.post<
      {
        sessionId: string
        interaction: Interaction
      },
      { candidateId: string; isPreview: boolean }
    >(
      `/simulations/${simulationId}/interactions/sessions`,
      {
        candidateId,
        isPreview,
      },
      {
        useFallbackAuth: true,
        params: {
          a: assessmentIds ?? [],
          t: testIds ?? [],
        },
      },
    )
    return response
  }

  async startInteractionSession(
    simulationId: string,
    sessionId: string,
    eventAttemptId?: string,
  ) {
    const response = await this.apiService.post<Thread, undefined>(
      `/simulations/${simulationId}/interactions/sessions/${sessionId}/start`,
      undefined,
      {
        useFallbackAuth: true,
        params: { eventAttempt: eventAttemptId },
      },
    )
    return response
  }

  async continueInteractionSession(
    simulationId: string,
    sessionId: string,
    thread: Thread,
  ) {
    const response = await this.apiService.post<Thread, Thread>(
      `/simulations/${simulationId}/interactions/sessions/${sessionId}/continue`,
      thread,
      { useFallbackAuth: true },
    )
    return response
  }

  async completeInteractionSession(simulationId: string, sessionId: string) {
    const response = await this.apiService.post<Thread, undefined>(
      `/simulations/${simulationId}/interactions/sessions/${sessionId}/complete`,
      undefined,
      { useFallbackAuth: true },
    )
    return response
  }

  async retrieveTranscript(simulationId: string, interactionId: string) {
    const response = await this.apiService.get<Thread>(
      `/simulations/${simulationId}/interactions/${interactionId}/retrieve-transcript`,
    )
    return response
  }
}
